import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Sparkle,
  Check,
  Buildings,
  Users,
  ChartLineUp,
  Bank,
  Receipt,
  ShieldCheck,
  Globe,
  StackSimple,
  ArrowRight,
  Lightning,
} from "@phosphor-icons/react";

const PLAN_ORDER = ["starter", "team", "group", "group_plus"];

const FEATURES = [
  { icon: Receipt,       title: "Double-entry ledger", body: "Sales, purchases, journals, credit notes — auto-posted with balance validation and full drill-down." },
  { icon: Bank,          title: "Bank imports + auto-match", body: "CSV, OFX, and AI-extracted PDF statements. Recognises invoice numbers and posts receipts against AR/AP for you." },
  { icon: ShieldCheck,   title: "HMRC Making Tax Digital", body: "One-click VAT returns, obligation tracker, auto-posted VAT liability journal on submission." },
  { icon: Globe,         title: "Multi-currency", body: "GBP, USD, EUR, NGN, GHS, KES, ZAR — with per-company FX rates and base-currency consolidation." },
  { icon: StackSimple,   title: "Group consolidation", body: "Merge multiple entities into a single P&L and balance sheet; intercompany balances eliminate automatically." },
  { icon: Lightning,     title: "Stripe & Paystack pay links", body: "Send an invoice with a hosted pay-link. Receipt journal posts automatically the moment payment lands." },
];

const TESTIMONIALS = [
  { quote: "We closed three companies in a single afternoon and filed VAT with one click. LedgerLoom is what accounting should feel like in 2026.", name: "Priya Shah", role: "Group Financial Controller" },
  { quote: "The bank auto-match alone saves my bookkeeper two days a month. And the consolidation is honestly better than Sage.", name: "Marcus Ellwood", role: "Founder, Ellwood Holdings" },
  { quote: "MTD used to be a nightmare with spreadsheets. We flipped to LedgerLoom and haven't missed a deadline since.", name: "Anna Kowalski", role: "Practice Manager, KOW Advisory" },
];

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState({});
  const [seatAddon, setSeatAddon] = useState(null);
  const [interval, setInterval] = useState("month");

  // Already authenticated → skip landing
  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    api.get("/subscription/plans").then((r) => {
      setPlans(r.data.plans || {});
      setSeatAddon(r.data.seat_addon || null);
    }).catch(() => {});
  }, []);

  const pickPlan = (planId) => {
    navigate(`/register?plan=${planId}&interval=${interval}`);
  };

  // While auth state is still loading (user === null) or we're already logged in, don't flash the landing
  if (user === null || user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="overline">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* NAV */}
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2" data-testid="landing-logo">
            <div className="w-8 h-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center">
              <Sparkle size={18} weight="fill" />
            </div>
            <span className="text-lg font-bold tracking-tight">LedgerLoom</span>
          </a>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
            <a href="#features" className="text-muted-foreground hover:text-foreground transition">Features</a>
            <a href="#pricing" className="text-muted-foreground hover:text-foreground transition" data-testid="landing-nav-pricing">Pricing</a>
            <a href="#testimonials" className="text-muted-foreground hover:text-foreground transition">Customers</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium hover:text-primary" data-testid="landing-login-link">Log in</Link>
            <Button size="sm" className="rounded-md" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })} data-testid="landing-cta-nav">
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section id="top" className="relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16 lg:pt-28 lg:pb-24">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 tag-badge mb-6">
              <Sparkle size={12} weight="fill" className="text-primary" />
              <span className="text-foreground">HMRC MTD-recognised · Multi-entity ready</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Precision bookkeeping,<br />
              <span className="text-primary">without the spreadsheet chaos.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
              LedgerLoom is the modern accounting platform for owners, bookkeepers, and group
              controllers. Multi-currency ledgers, HMRC Making Tax Digital, and group consolidation —
              all in one clean workspace.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" className="rounded-md h-12 px-6 text-base gap-2" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })} data-testid="landing-cta-primary">
                See pricing <ArrowRight size={18} weight="bold" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-md h-12 px-6 text-base" onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}>
                Explore features
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><Check size={16} className="text-primary" weight="bold" /> Cancel anytime</span>
              <span className="flex items-center gap-1.5"><Check size={16} className="text-primary" weight="bold" /> No card until you subscribe</span>
              <span className="flex items-center gap-1.5"><Check size={16} className="text-primary" weight="bold" /> Data export always free</span>
            </div>
          </div>

          {/* Hero visual: stats strip */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Entities managed", value: "1 — 10+" },
              { label: "Currencies supported", value: "7" },
              { label: "MTD-ready", value: "Live" },
              { label: "Bank formats", value: "CSV · OFX · PDF" },
            ].map((s) => (
              <div key={s.label} className="qb-card p-4">
                <div className="overline">{s.label}</div>
                <div className="mt-1 text-xl font-bold tracking-tight">{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl">
            <div className="overline mb-3">What's inside</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Everything a modern finance team actually uses.</h2>
            <p className="mt-3 text-muted-foreground">No feature bloat. Just the daily jobs — done faster.</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f) => (
              <div key={f.title} className="qb-card p-6" data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g,"-")}`}>
                <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-4">
                  <f.icon size={20} weight="fill" />
                </div>
                <div className="text-lg font-semibold">{f.title}</div>
                <div className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl">
          <div className="overline mb-3">Simple, honest pricing</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Pick a plan. Add seats when you grow.</h2>
          <p className="mt-3 text-muted-foreground">
            All plans include HMRC MTD, bank imports, invoicing, and support. Annual pricing gives you two months free.
          </p>
        </div>

        <div className="mt-8">
          <Tabs value={interval} onValueChange={setInterval}>
            <TabsList className="rounded-md">
              <TabsTrigger value="month" data-testid="landing-int-month">Monthly</TabsTrigger>
              <TabsTrigger value="year" data-testid="landing-int-year">Annual · save 2 months</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {PLAN_ORDER.filter((id) => plans[id]).map((id) => {
            const plan = plans[id];
            const price = plan.prices?.[interval];
            const isFeatured = id === "group";
            return (
              <div
                key={id}
                data-testid={`landing-plan-${id}`}
                className={`qb-card p-6 flex flex-col relative ${isFeatured ? "ring-2 ring-primary" : ""}`}
              >
                {isFeatured && (
                  <div className="absolute -top-3 left-6 tag-badge" style={{ background: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))", borderColor: "hsl(var(--primary))" }}>
                    Most Popular
                  </div>
                )}
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{plan.name}</div>
                <div className="text-xs text-muted-foreground mt-1">{plan.tagline}</div>
                <div className="mt-4 text-3xl font-bold tracking-tight num">{price?.label}</div>
                {price?.note && <div className="text-xs text-primary mt-1">{price.note}</div>}
                <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Buildings size={14} /> {plan.company_limit} {plan.company_limit === 1 ? "co." : "cos."}</span>
                  <span className="flex items-center gap-1"><Users size={14} /> {plan.user_limit} users</span>
                </div>
                <ul className="mt-5 space-y-2 text-sm flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={16} className="text-primary shrink-0 mt-0.5" weight="bold" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full mt-6 rounded-md h-11"
                  onClick={() => pickPlan(id)}
                  data-testid={`landing-choose-${id}`}
                  variant={isFeatured ? "default" : "outline"}
                >
                  Choose {plan.name}
                </Button>
              </div>
            );
          })}
        </div>

        {seatAddon && (
          <div className="mt-6 text-center text-sm text-muted-foreground">
            Need more logins? Add extra user seats for {seatAddon.prices[interval].label} — any time.
          </div>
        )}
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" className="bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="max-w-2xl">
            <div className="overline mb-3">From the front lines</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Finance teams that ship, use LedgerLoom.</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t) => (
              <blockquote key={t.name} className="qb-card p-6 flex flex-col">
                <div className="text-primary flex gap-0.5 mb-3">
                  {[0,1,2,3,4].map(i => <Sparkle key={i} size={14} weight="fill" />)}
                </div>
                <p className="text-sm leading-relaxed text-foreground flex-1">"{t.quote}"</p>
                <footer className="mt-4 pt-4 border-t border-border">
                  <div className="text-sm font-semibold">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{t.role}</div>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="qb-card p-10 lg:p-14 text-center relative overflow-hidden" style={{ background: "hsl(var(--sidebar))", color: "hsl(var(--sidebar-foreground))", borderColor: "hsl(var(--sidebar-border))" }}>
          <div className="absolute inset-0 grid-lines opacity-10 pointer-events-none" />
          <div className="relative">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Ready to close your books faster?</h2>
            <p className="mt-3 text-base opacity-80 max-w-xl mx-auto">
              Pick a plan and start posting to your ledger in under two minutes. No card required to explore.
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <Button
                size="lg"
                className="rounded-md h-12 px-6"
                onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="landing-cta-bottom"
              >
                See pricing
              </Button>
              <Link to="/login" className="text-sm underline opacity-90 hover:opacity-100">
                or sign in
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
              <Sparkle size={14} weight="fill" />
            </div>
            <span className="text-sm font-semibold">LedgerLoom</span>
            <span className="text-xs text-muted-foreground ml-2">© {new Date().getFullYear()} Westwick Row Ltd.</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <a href="#features" className="hover:text-foreground">Features</a>
            <a href="#pricing" className="hover:text-foreground">Pricing</a>
            <Link to="/login" className="hover:text-foreground">Log in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
